import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, X, Plus, Wand2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { platformLabel } from '@/lib/clipPlatformFormats';

const DAYS = [['sun', 'S'], ['mon', 'M'], ['tue', 'T'], ['wed', 'W'], ['thu', 'T'], ['fri', 'F'], ['sat', 'S']];

/**
 * Schedule → Batch (section 7) + Fill Next Available Slots (section 8).
 * Selecting items happens in the Content Queue panel (batch mode); this
 * panel configures the rules, previews the proposed placements, and lets
 * the creator adjust or confirm before anything is written to storage.
 */
export function ScheduleBatchPanel({
  selectedBatch, onRemoveFromBatch,
  dateRange, onDateRangeChange, postingDays, onTogglePostingDay,
  postingTimes, onAddPostingTime, onRemovePostingTime,
  onPreviewFill, filling, proposals, skipped, onAdjustProposalTime, onConfirmBatch, onCancelPreview,
}) {
  const [newTime, setNewTime] = useState('10:00');

  if (selectedBatch.length === 0) {
    return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="schedule-batch-empty">Turn on batch mode in the Content Queue and check a few items to build a batch here.</p>;
  }

  return (
    <div className="space-y-3" data-testid="schedule-batch-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1">Selected ({selectedBatch.length})</p>
        <div className="flex flex-wrap gap-1.5">
          {selectedBatch.map(({ variant, platform }) => (
            <Badge key={variant.id} variant="secondary" className="bg-white/10 text-white border-white/10 gap-1 pr-1" data-testid={`schedule-batch-selected-${variant.id}`}>
              {variant.contentTitle || variant.title} · {platformLabel(platform)}
              <button onClick={() => onRemoveFromBatch(variant.id)} className="hover:text-red-400"><X className="h-2.5 w-2.5" /></button>
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-[#8A93A6]">From</Label>
          <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" className="w-full justify-start border-white/10 mt-1 text-xs" data-testid="schedule-batch-start-trigger"><CalendarIcon className="h-3 w-3 mr-1.5" />{format(dateRange.start, 'MMM d')}</Button></PopoverTrigger>
            <PopoverContent className="p-0 w-auto"><Calendar mode="single" selected={dateRange.start} onSelect={(d) => d && onDateRangeChange({ ...dateRange, start: d })} /></PopoverContent></Popover>
        </div>
        <div>
          <Label className="text-[11px] text-[#8A93A6]">To</Label>
          <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" className="w-full justify-start border-white/10 mt-1 text-xs" data-testid="schedule-batch-end-trigger"><CalendarIcon className="h-3 w-3 mr-1.5" />{format(dateRange.end, 'MMM d')}</Button></PopoverTrigger>
            <PopoverContent className="p-0 w-auto"><Calendar mode="single" selected={dateRange.end} onSelect={(d) => d && onDateRangeChange({ ...dateRange, end: d })} /></PopoverContent></Popover>
        </div>
      </div>

      <div>
        <Label className="text-[11px] text-[#8A93A6]">Posting days</Label>
        <div className="flex gap-1 mt-1">
          {DAYS.map(([key, label]) => (
            <button key={key} onClick={() => onTogglePostingDay(key)} className={`h-7 w-7 rounded-full text-[10px] border ${postingDays.includes(key) ? 'bg-[#3A7BFF] border-[#3A7BFF] text-white' : 'border-white/10 text-[#8A93A6]'}`} data-testid={`schedule-batch-day-${key}`}>{label}</button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-[11px] text-[#8A93A6]">Preferred posting times</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {postingTimes.map((t) => (
            <Badge key={t} variant="secondary" className="bg-white/10 text-white border-white/10 gap-1 pr-1">{t}<button onClick={() => onRemovePostingTime(t)}><X className="h-2.5 w-2.5" /></button></Badge>
          ))}
        </div>
        <div className="flex gap-1.5 mt-1.5">
          <Input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="h-7 text-xs bg-black/40 border-white/10" data-testid="schedule-batch-time-input" />
          <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-white/10 shrink-0" onClick={() => onAddPostingTime(newTime)} data-testid="schedule-batch-add-time-button"><Plus className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {!proposals ? (
        <Button className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" onClick={onPreviewFill} disabled={filling || postingDays.length === 0 || postingTimes.length === 0} data-testid="schedule-batch-fill-button">
          {filling ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-2" />}Fill Next Available Slots
        </Button>
      ) : (
        <div className="space-y-2" data-testid="schedule-batch-proposals">
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Proposed placements — review before confirming</p>
          {proposals.map((p, i) => (
            <div key={`${p.variant.id}-${p.platform}`} className="flex items-center gap-2 text-xs p-1.5 rounded-lg border border-white/10" data-testid={`schedule-batch-proposal-${i}`}>
              <span className="truncate flex-1">{p.variant.contentTitle || p.variant.title} · {platformLabel(p.platform)}</span>
              <Input type="datetime-local" value={format(new Date(p.proposedAt), "yyyy-MM-dd'T'HH:mm")} onChange={(e) => onAdjustProposalTime(i, e.target.value)} className="h-7 text-[11px] bg-black/40 border-white/10 w-[170px]" data-testid={`schedule-batch-proposal-time-${i}`} />
            </div>
          ))}
          {skipped.length > 0 && (
            <div className="p-2 rounded-lg border border-amber-500/20 bg-amber-500/5">
              <p className="text-[10px] text-amber-300 mb-1">{skipped.length} item(s) skipped:</p>
              {skipped.map((s, i) => <p key={i} className="text-[10px] text-[#8A93A6]">{s.variant.contentTitle || s.variant.title} ({platformLabel(s.platform)}) — {s.reason}</p>)}
            </div>
          )}
          <div className="flex gap-2">
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onConfirmBatch} disabled={proposals.length === 0} data-testid="schedule-batch-confirm-button">Confirm Batch ({proposals.length})</Button>
            <Button variant="outline" className="border-white/10" onClick={onCancelPreview} data-testid="schedule-batch-cancel-button">Edit Rules</Button>
          </div>
        </div>
      )}
    </div>
  );
}
